import React, { useState, useEffect } from 'react'
import { trackPromise } from 'react-promise-tracker'

import { GetPlanComparison } from 'store/ChartApiClient'

import BreakdownStateRow from '../Components/BreakdownStateRow'
import { StyleSeries } from 'highcharts/HighchartsStyleHelper'
import {
    SetupDefaultOptions,
    FULL_HEIGHT_TWO_INPUT_ROW,
    DisableAllButFirstSeries,
} from 'highcharts/HighchartsHelper'
import LoadingWrapper from 'components/LoadingWrapper'
import DisplayRowInformation from '../../components/DisplayRowInformation'
import {
    IpsaBasicSupplementalBenefit,
    IpsaModerateSupplementalBenefit,
    IpsaComprehensiveSupplementalBenefit,
    DarkBackgroundColor,
    IpsaRed,
    IpsaWhite,
} from '../../../../../../highcharts/HighchartsStyleHelper'
import HighchartsWrapper from '../../../../../../highcharts/HighchartsWrapper'

const cat = ' CAT'

const PlanComparisonStep = ({ id }) => {
    const PromiseArea = 'graph-plan-comparison'

    const [breakdownStateValue, setBreakdownStateValue] = useState('Gross')
    const [coverageStateValue, setCoverageStateValue] = useState('None')
    const [durationStateValue, setDurationStateValue] = useState('Annual')

    const [options, setOptions] = useState({})
    const [chart, setChart] = useState({})
    const [error, setError] = useState(null)
    const [premiums, setPremiums] = useState({})
    const [isLoaded, setLoaded] = useState(false)

    const [showComprehensive, setShowComprehensive] = useState(false)
    const [showModerate, setShowModerate] = useState(false)
    const [showBasic, setShowBasic] = useState(false)

    const [showComprehensiveCat, setShowComprehensiveCat] = useState(false)
    const [showModerateCat, setShowModerateCat] = useState(false)
    const [showBasicCat, setShowBasicCat] = useState(false)

    const {
        basicPremium,
        moderatePremium,
        comprehensivePremium,
        basicName,
        moderateName,
        comprehensiveName,
    } = premiums

    const [premiumInfo, setPremiumInfo] = useState([
        {
            color: IpsaComprehensiveSupplementalBenefit,
            text: comprehensiveName ?? 'Comprehensive',
            value: comprehensivePremium,
            hide: true,
        },
        {
            color: IpsaModerateSupplementalBenefit,
            text: moderateName ?? 'Moderate',
            value: moderatePremium,
            hide: !showModerate,
        },
        {
            color: IpsaBasicSupplementalBenefit,
            text: basicName ?? 'Basic',
            value: basicPremium,
            hide: !showBasic,
        },
    ])

    const setCoverageSeriesVisibility = (
        seriesFiltered,
        currentColumn,
        groupCoverage,
        groupStdCoverage,
        indCoverage,
        incomeLoss
    ) => {
        seriesFiltered.forEach((_, i) => {
            if (
                (_.visible === true && _.name !== currentColumn) ||
                (_.visible === false && _.name === currentColumn)
            ) {
                //column is visible do nothing
            } else {
                //hidden columns need to be null
                //default first column with coverage
                if (groupCoverage[i] && i !== 1) {
                    groupCoverage[i] = null
                }
                if (groupStdCoverage[i] && i !== 1) {
                    groupStdCoverage[i] = null
                }
                if (indCoverage[i] && i !== 1) {
                    indCoverage[i] = null
                }
                if (incomeLoss[i]) {
                    incomeLoss[i] = null
                }
            }
        })
    }

    const removeTrailingNulls = (array) => {
        for (let i = array.length - 1; i >= 0; i--) {
            const item = array[i]
            if (item === null) {
                array.pop()
            } else {
                break
            }
        }
    }

    const setSeriesData = (series, data, direct = false) => {
        if (series) {
            //remove trailing nulls to allow column collapse
            removeTrailingNulls(data)
            // @ts-ignore
            if (direct) {
                series.data = data
            } else {
                series.setData(data, false)
            }
        }
    }

    const setCoverageAfterSelection = (series, currentColumn = '') => {
        let groupCoverage = []
        let groupStdCoverage = []
        let indCoverage = []
        let incomeLoss = []

        let groupSeries = undefined
        let groupStdSeries = undefined
        let indSeries = undefined
        let incomeLossSeries = undefined

        series.forEach((_) => {
            if (_.name === 'Group LTD Coverage') {
                groupSeries = _
                if (groupSeries && groupSeries.refData) {
                    groupCoverage = groupSeries.refData.slice(0)
                }
            }

            if (_.name === 'Group STD Coverage') {
                groupStdSeries = _
                if (groupStdSeries && groupStdSeries.refData) {
                    groupStdCoverage = groupStdSeries.refData.slice(0)
                }
            }

            if (_.name === 'Current Ind Coverage') {
                indSeries = _
                if (indSeries && indSeries.refData) {
                    indCoverage = indSeries.refData.slice(0)
                }
            }

            if (_.name === 'Income Loss') {
                incomeLossSeries = _
                if (incomeLossSeries && incomeLossSeries.refData) {
                    incomeLoss = incomeLossSeries.refData.slice(0)
                }
            }
        })

        const seriesFiltered = series
            .filter((_) => _.refName === 'showcoverage')
            .sort((_) => _.legendIndex)

        setCoverageSeriesVisibility(
            seriesFiltered,
            currentColumn,
            groupCoverage,
            groupStdCoverage,
            indCoverage,
            incomeLoss
        )

        setSeriesData(groupSeries, groupCoverage, true)
        setSeriesData(groupStdSeries, groupStdCoverage, true)
        setSeriesData(indSeries, indCoverage, true)
        setSeriesData(incomeLossSeries, incomeLoss, true)
    }

    const setCoverage = (series, currentColumn = '') => {
        let groupCoverage = []
        let groupStdCoverage = []
        let indCoverage = []
        let incomeLoss = []

        let groupSeries = undefined
        let groupStdSeries = undefined
        let indSeries = undefined
        let incomeLossSeries = undefined

        series.forEach((_) => {
            if (_.name === 'Group LTD Coverage') {
                groupSeries = _
                if (groupSeries.userOptions && groupSeries.userOptions.refData) {
                    groupCoverage = groupSeries.userOptions.refData.slice(0)
                }
            }

            if (_.name === 'Group STD Coverage') {
                groupStdSeries = _
                if (groupStdSeries.userOptions && groupStdSeries.userOptions.refData) {
                    groupStdCoverage = groupStdSeries.userOptions.refData.slice(0)
                }
            }

            if (_.name === 'Current Ind Coverage') {
                indSeries = _
                if (indSeries.userOptions && indSeries.userOptions.refData) {
                    indCoverage = indSeries.userOptions.refData.slice(0)
                }
            }

            if (_.name === 'Income Loss') {
                incomeLossSeries = _
                if (incomeLossSeries.userOptions && incomeLossSeries.userOptions.refData) {
                    incomeLoss = incomeLossSeries.userOptions.refData.slice(0)
                }
            }
        })

        const seriesFiltered = series
            .filter((_) => _.userOptions.refName === 'showcoverage')
            .sort((_) => _.userOptions.legendIndex)

        setCoverageSeriesVisibility(
            seriesFiltered,
            currentColumn,
            groupCoverage,
            groupStdCoverage,
            indCoverage,
            incomeLoss
        )

        setSeriesData(groupSeries, groupCoverage)
        setSeriesData(groupStdSeries, groupStdCoverage)
        setSeriesData(indSeries, indCoverage)
        setSeriesData(incomeLossSeries, incomeLoss)
    }

    const LegendClick = (legendName, visible, options, target) => {
        setCoverage(target.chart.series, legendName)
        setOptions(options)

        const seriesFiltered = target.chart.series.filter((_) => _.name === legendName)

        let mappedName = ''
        if (
            seriesFiltered &&
            seriesFiltered[0] &&
            seriesFiltered[0].userOptions &&
            seriesFiltered[0].userOptions.refData
        ) {
            mappedName = seriesFiltered[0].userOptions.refData
        }
        switch (mappedName) {
            case basicName:
                setShowBasic(visible)
                break
            case 'Basic':
                setShowBasic(visible)
                break
            case basicName + cat:
                setShowBasicCat(visible)
                break
            case 'Basic' + cat:
                setShowBasicCat(visible)
                break
            case moderateName:
                setShowModerate(visible)
                break
            case 'Moderate':
                setShowModerate(visible)
                break
            case moderateName + cat:
                setShowModerateCat(visible)
                break
            case 'Moderate' + cat:
                setShowModerateCat(visible)
                break
            case comprehensiveName:
                setShowComprehensive(visible)
                break
            case 'Comprehensive':
                setShowComprehensive(visible)
                break
            case comprehensiveName + cat:
                setShowComprehensiveCat(visible)
                break
            case 'Comprehensive' + cat:
                setShowComprehensiveCat(visible)
                break
            default:
                break
        }
    }

    useEffect(() => {
        setPremiumInfo([
            {
                color: IpsaComprehensiveSupplementalBenefit,
                text: comprehensiveName ?? 'Comprehensive',
                value: comprehensivePremium,
                hide: !showComprehensive,
            },
            {
                color: IpsaModerateSupplementalBenefit,
                text: moderateName ?? 'Moderate',
                value: moderatePremium,
                hide: !showModerate,
            },
            {
                color: IpsaBasicSupplementalBenefit,
                text: basicName ?? 'Basic',
                value: basicPremium,
                hide: !showBasic,
            },
        ])
    }, [
        showBasic,
        showComprehensive,
        showModerate,
        comprehensivePremium,
        moderatePremium,
        basicPremium,
    ])

    const GetTextColor = (seriesName) => {
        let color = ''
        switch (seriesName) {
            case basicName:
            case 'Basic':
            case basicName + cat:
            case 'Basic' + cat:
            case moderateName:
            case 'Moderate':
            case moderateName + cat:
            case 'Moderate' + cat:
            case comprehensiveName:
            case 'Comprehensive':
            case comprehensiveName + cat:
            case 'Comprehensive' + cat:
                color = DarkBackgroundColor
                break
            case 'Current Ind Coverage':
            case 'Current':
            case 'Group LTD Coverage':
                color = IpsaWhite
                break
            default:
                break
        }

        return color
    }

    useEffect(() => {
        const SetSeriesTextColor = (series, index, color) => {
            if (series && series[index]) {
                series[index].dataLabels = {
                    color: color,
                }
            }
        }

        const StyleGraph = (opts) => {
            opts = SetupDefaultOptions(opts, FULL_HEIGHT_TWO_INPUT_ROW, false)

            if (opts.series) {
                opts.series = opts.series.map((p) => StyleSeries(p))
            }

            opts = {
                ...opts,
                plotOptions: {
                    ...opts.plotOptions,
                    column: {
                        ...opts.plotOptions.column,
                        stacking: 'normal',
                        dataLabels: {
                            ...opts.plotOptions.column.dataLabels,
                            enabled: true,
                        },
                    },
                    series: {
                        events: {
                            legendItemClick: (e) => {
                                const {
                                    target: {
                                        name,
                                        chart,
                                        chart: { options, series },
                                        visible,
                                    },
                                } = e
                                LegendClick(name, !visible, options, e.target)
                            },
                        },
                        states: {
                            inactive: {
                                enabled: false,
                            },
                        },
                    },
                },
            }

            if (opts.series) {
                // set color
                SetSeriesTextColor(opts.series, 0, DarkBackgroundColor)
                SetSeriesTextColor(opts.series, 1, IpsaRed)
                for (let i = 2; i < opts.series.length; i++) {
                    SetSeriesTextColor(opts.series, i, GetTextColor(opts.series[i].name))
                }
            }

            if (!isLoaded) {
                opts.series = DisableAllButFirstSeries(opts.series)
            }

            if (chart && chart.series) {
                //hide series data after selection when column are not visibile
                chart.series.forEach((_, i) => {
                    opts.series[i].visible = _.visible
                })
                setCoverageAfterSelection(opts.series)
            }

            setLoaded(true)
            setOptions(opts)
        }

        trackPromise(
            GetPlanComparison(
                id,
                breakdownStateValue,
                durationStateValue,
                showBasicCat,
                showModerateCat,
                showComprehensiveCat
            )
                .then((resp) => {
                    const { data } = resp
                    StyleGraph(data)

                    const {
                        basicPremium,
                        moderatePremium,
                        comprehensivePremium,
                        basicName,
                        moderateName,
                        comprehensiveName,
                    } = data
                    setPremiums({
                        basicPremium,
                        moderatePremium,
                        comprehensivePremium,
                        basicName,
                        moderateName,
                        comprehensiveName,
                    })
                })
                .catch((err) => {
                    setError(err)
                })
                .finally(() => {}),
            PromiseArea
        )
    }, [
        breakdownStateValue,
        durationStateValue,
        id,
        showBasicCat,
        showComprehensiveCat,
        showModerateCat,
    ])

    if (error) {
        return <p>Something went wrong, try again later</p>
    }

    return (
        <LoadingWrapper area={PromiseArea}>
            <HighchartsWrapper
                options={options}
                chartCallback={(e) => {
                    setChart(e)
                }}
                printHeightPx={375}
            />
            <DisplayRowInformation items={premiumInfo} perMonthText />
            <BreakdownStateRow
                includeAccumulative={true}
                hideCoverage={true}
                breakdownValue={breakdownStateValue}
                coverageValue={coverageStateValue}
                durationValue={durationStateValue}
                breakdownChange={setBreakdownStateValue}
                coverageChange={setCoverageStateValue}
                durationChange={setDurationStateValue}
            />
        </LoadingWrapper>
    )
}

export default PlanComparisonStep
